import { Check, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";
import { tone, type Tone } from "@/components/ui/tone";
import { useT } from "@/lib/i18n";

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean | undefined;
};

export type SelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  id?: string | undefined;
  name?: string | undefined;
  tone?: Tone | undefined;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;

  placeholder?: string | undefined;
  className?: string | undefined;
  labelledBy?: string | undefined;
  describedBy?: string | undefined;

  searchable?: boolean | undefined;
};

const MAX_MENU_HEIGHT = 264;
const GAP = 6;

const ROW_HEIGHT = 44;
const LIST_CHROME = 10;

const SEARCH_HEIGHT = 48;

const SEARCH_FROM = 12;

type Placement = {
  left: number;
  width: number;
  maxHeight: number;
  side: "above" | "below";

  anchor: number;
};

function nextEnabled(options: SelectOption[], from: number, step: number): number {
  const count = options.length;
  for (let i = 1; i <= count; i += 1) {
    const index = (from + step * i + count * count) % count;
    if (options[index]?.disabled !== true) return index;
  }
  return from;
}

function filterOptions(options: SelectOption[], query: string): SelectOption[] {
  const wanted = query.trim().toLowerCase();
  if (wanted === "") return options;
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(wanted) || option.value.toLowerCase().includes(wanted),
  );
}

function firstEnabled(options: SelectOption[], step: number): number {
  const start = step > 0 ? 0 : options.length - 1;
  if (options[start]?.disabled !== true) return start;
  return nextEnabled(options, start, step);
}

export function Select({
  value,
  onChange,
  options,
  id,
  name,
  tone: which,
  disabled = false,
  invalid = false,
  placeholder,
  className,
  labelledBy,
  describedBy,
  searchable,
}: SelectProps) {
  const t = tone(which);
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const listId = `${generatedId}-listbox`;

  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const typed = useRef({ query: "", at: 0 });
  const translate = useT();

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState("");
  const [place, setPlace] = useState<Placement | null>(null);

  const [host, setHost] = useState<HTMLElement | null>(null);

  const selected = options.findIndex((option) => option.value === value);
  const chosen = selected === -1 ? undefined : options[selected];

  const canSearch = searchable ?? options.length > SEARCH_FROM;

  const visible = useMemo(
    () => filterOptions(options, canSearch ? query : ""),
    [options, query, canSearch],
  );

  const activeIndex = active < visible.length ? active : 0;

  const mounted = open && place !== null && host !== null;

  const measure = useCallback(() => {
    const element = trigger.current;
    if (element === null) return;
    const rect = element.getBoundingClientRect();
    const viewport = globalThis.innerHeight;
    const below = viewport - rect.bottom - GAP * 2;
    const above = rect.top - GAP * 2;

    const wanted = Math.min(
      MAX_MENU_HEIGHT + (canSearch ? SEARCH_HEIGHT : 0),
      Math.max(1, visible.length) * ROW_HEIGHT + LIST_CHROME + (canSearch ? SEARCH_HEIGHT : 0),
    );

    const flip = below < wanted && above > below;
    const room = flip ? above : below;
    const maxHeight = Math.max(120, Math.min(wanted, room));
    setPlace({
      left: rect.left,
      width: rect.width,
      maxHeight,
      side: flip ? "above" : "below",
      anchor: flip ? viewport - rect.top + GAP : rect.bottom + GAP,
    });
  }, [visible.length, canSearch]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    setHost(trigger.current?.closest("dialog") ?? document.body);
    measure();

    const onScroll = () => {
      measure();
    };
    globalThis.addEventListener("scroll", onScroll, true);
    globalThis.addEventListener("resize", onScroll);
    return () => {
      globalThis.removeEventListener("scroll", onScroll, true);
      globalThis.removeEventListener("resize", onScroll);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (trigger.current?.contains(target) === true) return;
      if (popup.current?.contains(target) === true) return;
      setOpen(false);
    };
    globalThis.addEventListener("pointerdown", onDown, true);
    return () => {
      globalThis.removeEventListener("pointerdown", onDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView({
      block: "nearest",
    });
  }, [open, activeIndex]);

  useEffect(() => {
    if (mounted && canSearch) search.current?.focus({ preventScroll: true });
  }, [mounted, canSearch]);

  const openWith = (index: number) => {
    if (disabled || options.length === 0) return;
    setQuery("");
    setActive(index);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const commit = (index: number) => {
    const option = visible[index];
    if (option === undefined || option.disabled === true) return;
    onChange(option.value);
    close();
    trigger.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      if (!open) {
        openWith(selected === -1 ? firstEnabled(options, step) : selected);
        return;
      }
      setActive(nextEnabled(visible, activeIndex, step));
      return;
    }
    if (!open) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openWith(selected === -1 ? firstEnabled(options, 1) : selected);
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      trigger.current?.focus();
      return;
    }

    if (event.key === "Enter" || (event.key === " " && !canSearch)) {
      event.preventDefault();
      commit(activeIndex);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      if (canSearch) return;
      event.preventDefault();
      setActive(firstEnabled(visible, event.key === "Home" ? 1 : -1));
      return;
    }
    if (event.key === "Tab") {
      close();
      return;
    }

    if (!canSearch && event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const now = Date.now();
      const typing =
        (now - typed.current.at < 1000 ? typed.current.query : "") + event.key.toLowerCase();
      typed.current = { query: typing, at: now };
      const found = visible.findIndex(
        (option) => option.disabled !== true && option.label.toLowerCase().startsWith(typing),
      );
      if (found !== -1) setActive(found);
    }
  };

  return (
    <>
      <button
        aria-controls={open ? listId : undefined}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid}
        aria-labelledby={labelledBy}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-xl border px-3.5 text-start text-sm transition-colors",

          "min-h-11 disabled:opacity-60",
          t.control,
          t.hover,
          invalid && "border-danger",
          open && "border-brand",
          className,
        )}
        disabled={disabled}
        id={triggerId}
        name={name}
        onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          event.preventDefault();
          if (open) {
            setOpen(false);
            return;
          }
          openWith(selected === -1 ? firstEnabled(options, 1) : selected);
        }}
        ref={trigger}
        role="combobox"
        type="button"
      >
        <span className={cn("min-w-0 truncate", chosen === undefined && t.muted)}>
          {chosen?.label ?? placeholder ?? ""}
        </span>
        <ChevronDown
          aria-hidden
          className={cn("h-4 w-4 shrink-0 transition-transform", open && "rotate-180", t.muted)}
        />
      </button>

      {mounted && place !== null && host !== null
        ? createPortal(
            <div
              className={cn("fixed z-50 flex flex-col rounded-xl border", t.popup)}
              ref={popup}
              style={{
                left: place.left,
                width: place.width,
                maxHeight: place.maxHeight,
                ...(place.side === "above" ? { bottom: place.anchor } : { top: place.anchor }),
              }}
            >
              {canSearch ? (
                <div className={cn("shrink-0 border-b p-1.5", t.line)}>
                  <input
                    aria-activedescendant={
                      visible.length === 0 ? undefined : `${listId}-${activeIndex}`
                    }
                    aria-autocomplete="list"
                    aria-controls={listId}
                    aria-label={translate("common.search")}
                    className={cn(
                      "w-full rounded-lg border bg-transparent px-2.5 py-1.5 text-sm outline-none",
                      t.control,
                      t.line,
                    )}
                    onChange={(event) => {
                      const next = event.target.value;
                      setQuery(next);
                      setActive(firstEnabled(filterOptions(options, next), 1));
                    }}
                    onKeyDown={onKeyDown}
                    placeholder={translate("common.search")}
                    ref={search}
                    type="text"
                    value={query}
                  />
                </div>
              ) : null}

              {visible.length === 0 ? (
                <p className={cn("px-3 py-3 text-sm", t.muted)}>{translate("common.noMatches")}</p>
              ) : null}

              <ul
                aria-labelledby={labelledBy ?? triggerId}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1"
                id={listId}
                ref={list}
                role="listbox"
                tabIndex={-1}
              >
                {visible.map((option, index) => {
                  const isSelected = option.value === value;
                  return (
                    <li
                      aria-disabled={option.disabled === true}
                      aria-selected={isSelected}
                      className={cn(
                        "flex min-h-11 items-center gap-2 px-3 text-sm",
                        option.disabled === true ? "opacity-50" : "cursor-pointer",
                        index === activeIndex && option.disabled !== true && t.active,
                        isSelected && "font-medium",
                      )}
                      data-index={index}
                      id={`${listId}-${index}`}
                      key={option.value}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        commit(index);
                      }}
                      onPointerEnter={() => {
                        if (option.disabled !== true) setActive(index);
                      }}
                      role="option"
                    >
                      <Check
                        aria-hidden
                        className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                      />
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>,
            host,
          )
        : null}
    </>
  );
}
